/**
 * A customized resize handle component for use with react-resizable-panels.
 * This component provides a draggable handle with a tooltip for resizing panels
 * within a PanelGroup layout.
 *
 * @example
 * ```tsx
 * <PanelGroup direction="horizontal">
 *   <Panel>Left panel content</Panel>
 *   <ResizeHandleWithTooltip />
 *   <Panel>Right panel content</Panel>
 * </PanelGroup>
 * ```
 *
 * The handle supports both horizontal and vertical resizing based on the direction prop,
 * and includes visual feedback during hover and drag interactions.
 */

import { memo, useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Box, styled } from '@mui/material';

import {
  PanelResizeHandle,
  PanelResizeHandleProps,
} from 'react-resizable-panels';

import Tooltip from '@watershed/ui-core/components/Tooltip';

const TOOLTIP_DELAYS = {
  enter: 800,
  leave: 0,
} as const;

const RESIZE_HANDLE = {
  handleWidth: 16,
  indicatorWidth: 0.5,
  indicatorHoverWidth: 1,
  // Set to 5px to centrally align against 1px wide indicator.
  overlayHandleWidth: 5,
  overlayHandleHeight: 80,
} as const;

const StyledPanelResizeHandle = styled(PanelResizeHandle, {
  shouldForwardProp: (prop) =>
    prop !== '$direction' && prop !== '$hideIndicatorLine',
})<{
  $direction?: 'vertical' | 'horizontal';
  $hideIndicatorLine?: boolean;
}>(({ theme, $direction = 'horizontal', $hideIndicatorLine = false }) => ({
  position: 'relative',
  // For vertical alignment, handle is horizontal (full width, fixed height)
  // For horizontal alignment, handle is vertical (fixed width, full height)
  width: $direction === 'vertical' ? '100%' : `${RESIZE_HANDLE.handleWidth}px`,
  height: $direction === 'vertical' ? `${RESIZE_HANDLE.handleWidth}px` : '100%',
  backgroundColor: 'transparent',
  zIndex: theme.zIndex.tooltip,
  margin:
    $direction === 'vertical'
      ? `-${RESIZE_HANDLE.handleWidth / 2}px 0`
      : `0 -${RESIZE_HANDLE.handleWidth / 2}px`,
  '&:focus, &:focus-visible': {
    outline: 'none',
  },

  ...(!$hideIndicatorLine && {
    '&::before': {
      content: '""',
      position: 'absolute',
      ...($direction === 'vertical'
        ? {
            left: 0,
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            height: `${RESIZE_HANDLE.indicatorWidth}px`,
          }
        : {
            top: 0,
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: `${RESIZE_HANDLE.indicatorWidth}px`,
          }),
      backgroundColor: theme.palette.grey30,
      borderRadius: theme.shape.borderRadius,
    },

    '&[data-resize-handle-active="pointer"]::before': {
      backgroundColor: theme.palette.grey30,
      ...($direction === 'vertical'
        ? { height: `${RESIZE_HANDLE.indicatorHoverWidth}px` }
        : { width: `${RESIZE_HANDLE.indicatorHoverWidth}px` }),
    },
  }),

  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width:
      $direction === 'vertical'
        ? `${RESIZE_HANDLE.overlayHandleHeight}px`
        : `${RESIZE_HANDLE.overlayHandleWidth}px`,
    height:
      $direction === 'vertical'
        ? `${RESIZE_HANDLE.overlayHandleWidth}px`
        : `${RESIZE_HANDLE.overlayHandleHeight}px`,
    backgroundColor: theme.palette.midnight,
    borderRadius: theme.shape.borderRadius * 2,
    opacity: 0,
    transition: theme.transitions.create(['opacity', 'transform'], {
      duration: theme.transitions.duration.standard,
    }),
  },

  '&[data-resize-handle-active="pointer"]::after': {
    opacity: 1,
  },

  '&:hover': {
    ...(!$hideIndicatorLine && {
      '&::before': {
        backgroundColor: theme.palette.grey30,
        ...($direction === 'vertical'
          ? { height: `${RESIZE_HANDLE.indicatorHoverWidth}px` }
          : { width: `${RESIZE_HANDLE.indicatorHoverWidth}px` }),
      },
    }),
    '&::after': {
      opacity: 1,
      transform: 'translate(-50%, -50%) scale(1.2)',
    },
  },
  '&[data-resize-handle-active="pointer"]:hover::after': {
    transform: 'translate(-50%, -50%)',
  },
}));

export const ResizeHandleWithTooltip = memo(
  ({
    direction = 'horizontal',
    hideIndicatorLine = false,
    ...props
  }: PanelResizeHandleProps & {
    direction?: 'vertical' | 'horizontal';
    hideIndicatorLine?: boolean;
  }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Only handle hover events on the blue indicator
    const handleMouseEnter = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, TOOLTIP_DELAYS.enter);
    };

    const handleMouseLeave = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(false);
      }, TOOLTIP_DELAYS.leave);
    };

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <Tooltip
        title={t`Drag to resize`}
        placement={direction === 'vertical' ? 'top' : 'left'}
        open={showTooltip && !isDragging}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -2],
                },
              },
            ],
            style: {
              pointerEvents: 'none',
            },
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'flex',
            cursor: direction === 'vertical' ? 'row-resize' : 'col-resize',
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <StyledPanelResizeHandle
            {...props}
            $direction={direction}
            $hideIndicatorLine={hideIndicatorLine}
            onDragging={(dragging) => {
              setIsDragging(dragging);
              props.onDragging?.(dragging);
            }}
          />
        </Box>
      </Tooltip>
    );
  }
);
